import AgeIcon from 'assets/icons/commonsIcon/age_icon.svg?react';
import AirPollutionRemovalIcon from 'assets/icons/commonsIcon/air_pollution_removal.svg?react';
import BiodiversityIndexIcon from 'assets/icons/commonsIcon/biodiversity_index_icon.svg?react';
import CarbonStorageIcon from 'assets/icons/commonsIcon/carbon_storage_icon.svg?react';
import CoolingPowerIcon from 'assets/icons/commonsIcon/cooling_power_icon.svg?react';
import GenericTreeIcon from 'assets/icons/commonsIcon/generic_tree_icon.svg?react';
import HeightIcon from 'assets/icons/commonsIcon/height_icon.svg?react';
import EcoregionIcon_Pa12686 from 'assets/icons/commonsIcon/pa12-686_western_european_broadleaf_forests.svg';
import StormWaterManagementIcon from 'assets/icons/commonsIcon/storm_water_management.svg?react';
import MultilingualText from 'components/Multilingual/MultilingualText';
import GirthIcon from 'components/svg/GirthIcon';
import IntlMessages from 'helpers/IntlMessages';
import { getPictureLocaleUrl } from 'helpers/Utils';
import { specimenEcosystemServices } from 'helpers/ecosystem-services/specimenEcosystemServices';
import { roundToThousands } from 'helpers/utils-typescript';
import ChevronLeftIcon from 'mdi-react/ChevronLeftIcon';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import EditOutlineIcon from 'mdi-react/EditOutlineIcon';
import { SpecimenDetailMobileGeneralProps } from 'pages/Bioregion/Specimen//SpecimenDetailsMobileLayout';
import React, { Fragment, ReactElement, useMemo, useState } from 'react';
import { Card, Row } from 'react-bootstrap';
import Lightbox from 'react-image-lightbox';
import { useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';


interface SpecimenOverviewProps extends SpecimenDetailMobileGeneralProps {
  setSpecimenData?: React.Dispatch<React.SetStateAction<{
    dataWtUnits: string,
    icon: ReactElement<{ height: string, className: string }>,
    titleTranslationId: string,
  }>>;
  isPrintableCard: boolean,
}

interface SingleEcoServiceCardProps {
  data: number | string,
  icon: ReactElement<{ height: string, className: string }>,
  isPrintableCard: boolean,
}

export const SpecimenCombinedBasicInfoCard = (
  {
    basicInfoArray,
    setContextHelpId,
    setShowContextHelpModal,
    setSpecimenData,
  }: {
    basicInfoArray: {
      statisticContent: any,
      Icon: ReactElement<{ height: string, className: string }>
      contextHelpId: string,
      titleTranslationId: string,
      contextHelpIcon: ReactElement<{ height: string, className: string }>
    }[],
    setSpecimenData: React.Dispatch<React.SetStateAction<{
      dataWtUnits: string,
      icon: ReactElement<{ height: string, className: string }>,
      titleTranslationId: string,
    }>>;
    setContextHelpId: React.Dispatch<React.SetStateAction<string>>,
    setShowContextHelpModal: React.Dispatch<React.SetStateAction<boolean>>,
  }
) => {
  return (
    <div
      className='my-auto'
      style={{ height: `${basicInfoArray.length / (basicInfoArray.length + 2) * 100}%` }}
    >
      {
        basicInfoArray.map((basicInfo, index) => {
          return (
            <div
              className='d-flex flex-column py-2 justify-content-center'
              key={index}
              style={{ height: `${1 / basicInfoArray.length * 100}%`, minHeight: '4rem' }} //py-2: 1rem, stats label: 1rem, desired min-height of icon: 2rem
              onClick={() => {
                setContextHelpId(basicInfo.contextHelpId);
                setShowContextHelpModal(true);
                setSpecimenData({
                  dataWtUnits: basicInfo.statisticContent,
                  icon: basicInfo.contextHelpIcon,
                  titleTranslationId: basicInfo.titleTranslationId
                });
              }}
            >
              {basicInfo.Icon}
              <div className='text-center text-truncate' style={{ maxWidth: '100%', lineHeight: 1 }}>
                {basicInfo.statisticContent}
              </div>
            </div>
          );
        }
        )}
    </div>
  );
};

const SingleEcoServiceCard = ({
  data,
  icon,
  isPrintableCard
}: SingleEcoServiceCardProps
) => {
  return (
    <div className='d-flex flex-column'
      style={{ maxHeight: '100%' }}
    >
      {icon}
      <div className='text-center' style={{ fontSize: isPrintableCard ? '1rem' : '0.8rem' }}>
        {data}
      </div>
    </div>
  );
};

const SpecimenOverviewTabMobile = ({
  goBackLink,
  hasEditPermission,
  specimanBySlug,
  setContextHelpId,
  setShowContextHelpModal,
  setSpecimenData,
  isPrintableCard,
  showInQuestStop,
}: SpecimenOverviewProps) => {

  const { slug, questSlug } = useParams() as any;
  const [showLightBox, setShowLightBox] = useState(false);
  const intl = useIntl();

  // cover pic url
  const specimenPicUrl = specimanBySlug?.coverPictureUrl
    ? getPictureLocaleUrl(specimanBySlug?.coverPictureUrl)
    : specimanBySlug?.specimenAttachmentsBySpecimenId?.nodes[0]?.url;



  // ecosystem services
  const [ecoServiceInfoArray, specimenHeight, specimenAge] = useMemo(() => {

    const {
      height: specimenHeight,
      age: specimenAge,
      co2FixationLifeTime: specimenCo2FixationLifeTime,
      // coolingByTranspiration: specimenCoolingByTranspiration, // for now we are switching to coolingByShading from iTree
      coolingByShading: specimenCoolingByShading,
      airPollutionRemoval: specimenAirPollutionRemoval,
      stormWaterManagement: specimenStormWaterManagement,
    } = specimenEcosystemServices(
      specimanBySlug?.species?.scientificName,
      specimanBySlug?.girthCm,
      specimanBySlug?.surfaceSealing,
      specimanBySlug?.itreeApiJson
    );

    const ecoServiceInfoArray = [
      {
        data: roundToThousands(specimenCo2FixationLifeTime) ?? '-',
        icon: <CarbonStorageIcon height={'100%'} style={{ aspectRatio: '1/1', minWidth: '20px' }} className={`${specimenCo2FixationLifeTime ? '' : 'icon-disabled'} mx-auto`} />,
        contextHelpId: '486167ed-c2a7-4fa8-b609-98f82c99ac33',
        titleTranslationId: 'specimen.carbon-storage',
        contextHelpIcon: <CarbonStorageIcon height={'60px'} className={`${specimenCo2FixationLifeTime ? '' : 'icon-disabled'} mx-auto my-2`} />,
        dataWtUnits: intl.formatMessage({ id: specimenCo2FixationLifeTime ? 'specimen.carbon-storage-data' : 'specimen.no-data-available' }, { carbonStorageData: roundToThousands(specimenCo2FixationLifeTime) }),
      },
      {
        data: roundToThousands(specimenCoolingByShading as number) ?? '-',
        icon: <CoolingPowerIcon height={'100%'} style={{ aspectRatio: '1/1', minWidth: '20px' }} className={`${specimenCoolingByShading ? '' : 'icon-disabled'} mx-auto`} />,
        contextHelpId: '0400c248-6e59-4015-a42c-5fd0f2f3d172',
        titleTranslationId: 'specimen.cooling-power',
        contextHelpIcon: <CoolingPowerIcon height={'60px'} className={`${specimenCoolingByShading ? '' : 'icon-disabled'} mx-auto my-2`} />,
        dataWtUnits: intl.formatMessage({ id: specimenCoolingByShading ? 'specimen.cooling-power-data' : 'specimen.no-data-available' }, { coolingPowerData: roundToThousands(specimenCoolingByShading as number) }),
      },
      {
        data: specimanBySlug?.biodiversityRichness ?? '-',
        icon: <BiodiversityIndexIcon height={'100%'} style={{ aspectRatio: '1/1', minWidth: '20px' }} className={`${specimanBySlug?.biodiversityRichness ? '' : 'icon-disabled'} mx-auto`} />,
        contextHelpId: '2c196383-d88f-4689-94c7-af71116728ff',
        titleTranslationId: 'specimen.biodiversity-richness',
        contextHelpIcon: <BiodiversityIndexIcon height={'60px'} className={`${specimanBySlug?.biodiversityRichness ? '' : 'icon-disabled'} mx-auto my-2`} />,
        dataWtUnits: specimanBySlug?.biodiversityRichness?.toString() ?? intl.formatMessage({ id: 'specimen.no-data-available' }),
      },
      {
        data: roundToThousands(specimenStormWaterManagement as number) ?? '-',
        icon: <StormWaterManagementIcon height={'100%'} style={{ aspectRatio: '1/1', minWidth: '20px' }} className={`${specimenStormWaterManagement ? '' : 'icon-disabled'} mx-auto`} />,
        contextHelpId: '55ee370a-b411-45ef-b2b3-d7b92324aa2a',
        titleTranslationId: 'specimen.water-management',
        contextHelpIcon: <StormWaterManagementIcon height={'60px'} className={`${specimenStormWaterManagement ? '' : 'icon-disabled'} mx-auto my-2`} />,
        dataWtUnits: intl.formatMessage({ id: specimenStormWaterManagement ? 'specimen.water-management-data' : 'specimen.no-data-available' }, { waterManagementData: roundToThousands(specimenStormWaterManagement as number) }),
      },
      {
        data: roundToThousands((specimenAirPollutionRemoval as number) * 1000, 0) ?? '-',
        icon: <AirPollutionRemovalIcon height={'100%'} style={{ aspectRatio: '1/1', minWidth: '20px' }} className={`${specimenAirPollutionRemoval ? '' : 'icon-disabled'} mx-auto`} />,
        contextHelpId: '33ee616a-039d-4b01-8713-f80f73b2d1ab',
        titleTranslationId: 'specimen.air-pollution-removal',
        contextHelpIcon: <AirPollutionRemovalIcon height={'60px'} className={`${specimenAirPollutionRemoval ? '' : 'icon-disabled'} mx-auto my-2`} />,
        dataWtUnits: intl.formatMessage({ id: specimenAirPollutionRemoval ? 'specimen.air-pollution-removal-data' : 'specimen.no-data-available' }, { airPollutionRemovalData: roundToThousands((specimenAirPollutionRemoval as number) * 1000, 2) }),

      },
    ];
    return [ecoServiceInfoArray, specimenHeight, specimenAge];
  }, [specimanBySlug, intl]);

  return (
    <Fragment>
      <Card.Body className='p-0' style={{ height: '65%', minHeight: '20rem' }}>
        <Row className='no-gutter m-0' style={{ maxHeight: '100%', height: '100%' }}>

          {/* specimen cover picture */}
          <div className='col-9 px-0' style={{ maxHeight: '100%', height: '100%', }}>

            {/* Buttons don't show on printable card or in quest stop complete page*/}
            {!isPrintableCard && !showInQuestStop &&
              <div
                className='d-flex justify-content-between p-2'
                style={{ position: 'absolute', top: '0', left: '0', width: '100%', zIndex: '1' }}
              >
                {/* Back button */}
                <Link
                  to={goBackLink}
                  className='btn btn-xs rounded-circle badge-outline-dark p-0'
                  style={{ aspectRatio: '1/1', backgroundColor: 'white' }}>
                  <ChevronLeftIcon size={30} color='#909090' />
                </Link>

                {/* Edit button */}
                {hasEditPermission &&
                  <Link
                    to={`/commons/${specimanBySlug.territory?.slug}/specimen/edit/${slug}`} // TODO: maybe broken for specimens without a commons
                    className='btn btn-xs rounded-circle badge-outline-orange p-0'
                    style={{ aspectRatio: '1/1', backgroundColor: '#EFB700' }}>

                    <EditOutlineIcon size={30} className='p-1' color='white' />
                  </Link>}

              </div>
            }
            {/* Cover pic */}
            <div
              className={`position-relative mb-1 ${!isPrintableCard && 'card-img-top-left-bottom-right-rounded'}`}
              style={{
                overflow: 'hidden',
                maxHeight: '100%',
                height: '100%',
                backgroundImage: `url(${specimenPicUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
              onClick={() => { setShowLightBox(true); }}
            />
            {(showLightBox)
              && <Lightbox
                mainSrc={specimenPicUrl}
                onCloseRequest={() => { setShowLightBox(false); }}
              />
            }
          </div>

          {/* specimen basic info and ecoregion info */}

          <div className='col-3 px-2 d-flex flex-column' style={{ height: '100%', minHeight: '20rem' }}> {/* min height is calculated based on the desired min height of child element = 4rem */}

            {/* ecoregion avatar */}
            <img
              src={EcoregionIcon_Pa12686}
              alt='ecoregion avatar'
              className='rounded-circle py-1 my-auto'
              style={{ maxHeight: '20%', aspectRatio: '1/1' }}
            />
            {/* commons avatar */}
            <div style={{ maxHeight: '20%', aspectRatio: '1/1' }} className='my-auto py-1 d-flex'>
              <img
                src={specimanBySlug.territory?.avatarUrl ?? '/assets/img/profiles/l-1.jpg'}
                alt={specimanBySlug.territory?.name?.en}
                className='rounded-circle mx-auto'
                style={{ objectFit: 'cover', height: '100%', maxWidth: '100%', }}
              />
            </div>


            {/* basic info */}
            <SpecimenCombinedBasicInfoCard
              basicInfoArray={[
                {
                  statisticContent: `${specimenAge || specimanBySlug.ageYears || '?'} years`,
                  Icon: <AgeIcon height={'calc( 100% - 1.2rem)'} className='mx-auto my-auto' />,
                  contextHelpId: '97d860ca-a5a1-44f5-a174-9380dafea890',
                  titleTranslationId: 'specimen.age',
                  contextHelpIcon: <AgeIcon height={'60px'} className='mx-auto my-auto' />,
                },
                {
                  statisticContent: `${specimanBySlug.girthCm || '?'} cm`,
                  Icon: <GirthIcon color="#60b6aa" size={'calc( 100% - 1.2rem)'} className="mx-auto  my-auto" strokeWidth={'1'} />,
                  contextHelpId: '3c716d75-065c-4d3e-a024-7f969e7ba129',
                  titleTranslationId: 'specimen.girth',
                  contextHelpIcon: <GirthIcon size={'60px'} className='mx-auto my-auto' strokeWidth={'1'} />,
                },
                {
                  statisticContent: `${specimenHeight} m`,
                  Icon: <HeightIcon height={'calc( 100% - 1.2rem)'} className='mx-auto  my-auto' />,
                  contextHelpId: '16b77b3b-5e38-4d9d-9979-1fc549d0fd89',
                  titleTranslationId: 'specimen.height',
                  contextHelpIcon: <HeightIcon height={'60px'} className='mx-auto my-auto' />,
                },
              ]}
              setContextHelpId={setContextHelpId}
              setShowContextHelpModal={setShowContextHelpModal}
              setSpecimenData={setSpecimenData}
            />
          </div>
        </Row>
      </Card.Body>


      <Card.Body className='px-0 pt-0 d-flex flex-column my-auto ' style={{ height: '35%' }}>

        {/* Specimen Name and Code */}
        <Row className='no-gutter m-0 py-2 my-auto'> {/* mb-auto align the row to top */}
          <div className='col-9 px-0'>
            <div className='font-weight-bold ml-3 text-truncate' style={{ maxWidth: '100%', fontSize: '1.5rem', lineHeight: '2.25rem' }}>
              <MultilingualText value={specimanBySlug.name} />
            </div>
          </div>
          <div className='col-3'>
            <div className='font-weight-light vertical-center text-center font-italic ml-n1 ' style={{ maxWidth: '100%', fontSize: '1rem', fontWeight: '100' }}>
              {specimanBySlug.code}
            </div>
          </div>
        </Row>

        <div className='border-bottom my-auto' /> {/* my-auto align the div in center */}

        {/* Species Info printable card has slightly different layout*/}
        {!isPrintableCard ?
          <Link
            className='d-flex flex-row flex-noWrap align-items-center py-2 w-100 my-auto' //* my-auto align the div in center */}
            to={
              {
                pathname: specimanBySlug.species ? `/species/${specimanBySlug.species.slug}` : `/commons/${specimanBySlug.territory.slug}/specimen/edit/${slug}`,
                state: showInQuestStop && { breadcrumbsTranslationId: 'route.quest', from: `/quest/${questSlug}/map` }
              }
            }
          >
            <div className='mr-auto d-flex ml-3 flex-fill' style={{ minWidth: '0' }}> {/* if an element styled with 'text-truncate' is not direct child of a flex element, then its parent component must have min-width = 0 or some actual value */}
              <GenericTreeIcon height={'2.25rem'} className={'my-auto'} />
              <div className='d-flex flex-column mr-auto ml-3 my-auto' style={{ minWidth: '0' }}> {/* if an element styled with 'text-truncate' is not direct child of a flex element, then its parent component must have min-width = 0 or some actual value */}
                {specimanBySlug.species
                  ? <Fragment>
                    <div className='h4 mb-0 color-theme-1 text-truncate' style={{ lineHeight: '1.5rem', maxWidth: '100%' }}>
                      <MultilingualText value={specimanBySlug.species.name} />
                    </div>
                    <div className='color-theme-1 text-truncate font-italic ' style={{ lineHeight: '1.25rem', maxWidth: '100%' }}>
                      {specimanBySlug.species.scientificName}&nbsp;
                    </div>
                  </Fragment>
                  : <IntlMessages id={'specimen.unknown-species'} />
                }
              </div>
            </div>
            <ChevronRightIcon size={'40'} color='#60b6aa' className='flex-fill' style={{ maxWidth: '25%' }} />
          </Link>

          : <Row className='no-gutters my-auto'>
            <div className='col-9 d-flex flex-column my-auto pl-3' style={{ minWidth: '0' }}> {/* if an element styled with 'text-truncate' is not direct child of a flex element, then its parent component must have min-width = 0 or some actual value */}
              {specimanBySlug.species
                ? <Fragment>
                  <div className='h4 mb-0 color-theme-1 text-truncate' style={{ lineHeight: '1.5rem', maxWidth: '100%' }}>
                    <MultilingualText value={specimanBySlug.species.name} />
                  </div>
                  <div className='color-theme-1 text-truncate font-italic ' style={{ lineHeight: '1.25rem', maxWidth: '100%' }}>
                    {specimanBySlug.species.scientificName}&nbsp;
                  </div>
                </Fragment>
                : <IntlMessages id={'specimen.unknown-species'} />
              }
            </div>
            <div className='col-3 d-flex'>
              <GenericTreeIcon height={'2.25rem'} className='my-auto mx-auto' />
            </div>
          </Row>
        }

        <div className='border-bottom my-auto' /> {/* my-auto align the div in center */}

        <div
          className='d-flex flex-row flex-nowrap pt-2 my-auto' //* my-auto align the div in center */
          style={{ maxHeight: 'calc(100% - 2.25rem - 0.75rem - 2.75rem)' }} /* pt-2 = 0.5rem, pb-1=0.25rem, 2.25rem is the lineHeight of the specimen name, 2.75rem is the lineHeight of the species info*/
        >

          {specimanBySlug.isNatureMonument
            ?
            <div className="d-flex flex-column mx-2 alert alert-warning w-100" style={{ 'color': '#856404' }} role="alert">
              This specimen was imported and there is a lot of information missing! How about you navigate to it and collect the missing data?
            </div>
            : ecoServiceInfoArray.map((ecoServiceInfo, index) => {
              const { titleTranslationId, contextHelpIcon, data, icon, contextHelpId, dataWtUnits } = ecoServiceInfo;
              return (
                <div
                  key={index}
                  className='mx-2'
                  style={{ maxHeight: '100%', width: '20%' }}
                  onClick={() => {
                    setContextHelpId(contextHelpId);
                    setShowContextHelpModal(true);
                    setSpecimenData({ titleTranslationId, icon: contextHelpIcon, dataWtUnits });
                  }}
                >
                  <SingleEcoServiceCard
                    data={data}
                    icon={icon}
                    isPrintableCard={isPrintableCard}
                  />
                </div>
              );
            })}

        </div>
      </Card.Body>
    </Fragment>
  );
};

export default SpecimenOverviewTabMobile;
